/**
 * RFC 9562 UUID (versions 1-8), matching the backend's `z.uuid()` constraint,
 * so an id accepted on the client is never one the backend would drop. Shared
 * by every first-party identity cookie validator (`_aid` in lib/auth0 and
 * `ph_anon_id` in lib/analytics) so they cannot drift apart.
 */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * A random version 4 UUID that also works where `crypto.randomUUID` does not
 * (Safari before 15.4), so a caller never throws on an older browser.
 */
export const randomUuid = (): string => {
  const webCrypto = typeof crypto === 'undefined' ? undefined : crypto;
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    // Built from crypto.getRandomValues, which exists wherever randomUUID
    // does not. Version and variant bits are set with modulo arithmetic (no
    // bitwise).
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) % 16) + 0x40; // version 4 (high nibble)
    bytes[8] = ((bytes[8] ?? 0) % 64) + 0x80; // variant 10xx (high bits)
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join('')
    ].join('-');
  }

  // Last resort with no Web Crypto at all (never in a browser): Math.random.
  const hex = (length: number): string =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${variant}${hex(3)}-${hex(12)}`;
};
